import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Collapse, TableCell, TableRow } from '@mui/material';

import OnFileDownloadIcon from '../../../../icons/OnFileDownloadIcon';
import {
    selectPoolStatusesForAccepted,
    selectPoolStatusesForRejected
} from '../../../../store/dataSelectors';
import {
    loadContractDetailsForContractId,
    loadServiceDetailsForContractId
} from '../../../../store/directusService';
import { downloadParticipantDocument } from '../../../../store/documentService';
import { loadParticipantByPtCode } from '../../../../store/participantService';
import { formatDate } from '../../../../utils/dateFunctions';
import SingleSelect from '../../../formElements/SingleSelect';

import RejectionDetails from './RejectionDetails';

import table from '../../../../commonStyles/tables.module.css';
import poolClasses from '../../recruitmentStyles/pool.module.css';

const rowStyles = {
    paddingBottom: 0,
    paddingTop: 0,
    paddingLeft: 0,
    paddingRight: 0,
    borderTop: 'none',
    borderBottom: 'none'
};

const PoolTableRow = ({
    row,
    isActive,
    toggleDropdown,
    openId,
    onUpdateParticipantStatus,
    onPoolApplicationRejectEditor,
    onPoolRejectedRow,
    rowCancelled
}) => {
    // HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE
    const [poolStatus, setPoolStatus] = useState({});
    const [newStatuses, setNewStatuses] = useState([]);
    const [isPTRequestSent, setIsPTRequestSent] = useState(false);

    // STORE STATE
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const poolStatusDetails = useSelector(
        (state) => state.entities.directusService.poolStatusDetails
    );
    const poolStatusesForAccepted = useSelector(selectPoolStatusesForAccepted);
    const poolStatusesForRejected = useSelector(selectPoolStatusesForRejected);
    const contractDetailsById = useSelector(
        (state) => state.entities.directusService.contractDetailsById
    );
    const servicesByContractId = useSelector(
        (state) => state.entities.directusService.servicesByContractId
    );

    // USE EFFECTS
    useEffect(() => {
        if (isPTRequestSent && currentParticipant?.ptCode === row.ptCode) {
            if (!(currentParticipant.contractId in contractDetailsById))
                dispatch(loadContractDetailsForContractId(currentParticipant.contractId));
            if (!(currentParticipant.contractId in servicesByContractId))
                dispatch(loadServiceDetailsForContractId(currentParticipant.contractId));
        }
    }, [isPTRequestSent, currentParticipant]);

    useEffect(() => {
        if (
            isPTRequestSent &&
            currentParticipant?.ptCode === row.ptCode &&
            contractDetailsById[currentParticipant.contractId] &&
            servicesByContractId[currentParticipant.contractId]
        )
            navigate('/edit_participant');
    }, [isPTRequestSent, contractDetailsById, servicesByContractId, currentParticipant]);

    useEffect(() => {
        if (!poolStatusDetails?.length) return;
        setPoolStatus(row?.status || {});
        if (row?.status?.name?.toLowerCase() === 'requested') setNewStatuses(poolStatusDetails);
        else if (row?.status?.name?.toLowerCase() === 'accepted')
            setNewStatuses(poolStatusesForAccepted);
        else if (row?.status?.name?.toLowerCase() === 'rejected')
            setNewStatuses(poolStatusesForRejected);
    }, [row, poolStatusDetails]);

    useEffect(() => {
        rowCancelled && setPoolStatus(row?.status);
    }, [rowCancelled]);

    // EVENT HANDLERS
    const onClickPTCode = (e) => {
        e.stopPropagation();
        if (!row?.ptCode) return;
        dispatch(loadParticipantByPtCode(row.ptCode));
        setIsPTRequestSent(true);
    };

    const onToggleDropdown = () => toggleDropdown(openId === row.id ? '' : row.id);

    const onDownload = () => dispatch(downloadParticipantDocument(row.participantId, row.filename));

    const onPoolStatusChange = (id) => {
        const selected = poolStatusDetails.find((el) => el.id === id);

        if (poolStatus?.id && poolStatus.id !== id) {
            onUpdateParticipantStatus(id, row.participantId, row.participantPoolApplicationId);
            if (
                selected && selected.name.toLowerCase() === 'rejected' &&
                row.status?.name?.toLowerCase() !== 'rejected'
            ) {
                onPoolRejectedRow(row);
                onPoolApplicationReject();
            }
        }
        selected && setPoolStatus(selected);
    };

    const onPoolApplicationReject = () => onPoolApplicationRejectEditor(true);

    // RENDER
    return (
        <>
            <TableRow
                key={row.id}
                hover
                className={`${table.canOpen} ${table.rowHeight}`}
                onClick={onToggleDropdown}>
                <TableCell
                    className={`${poolClasses.poolCodeLink} ${table.rowLink}`}
                    onClick={onClickPTCode}
                    disabled={!isActive}>
                    {row?.ptCode}
                </TableCell>
                <TableCell>{row.ptName ? row.ptName : ''}</TableCell>
                <TableCell>{row?.filename || ''}</TableCell>
                <TableCell className={table.cellIcon}>
                    <OnFileDownloadIcon
                        active={isActive && row?.filename !== '' && row?.filename !== undefined}
                        onDownload={onDownload}
                    />
                </TableCell>
                <TableCell>
                    <span className={table.cellDate}>
                        {row?.startDate ? formatDate(row?.startDate) : '-'}
                    </span>
                </TableCell>
                <TableCell>{row?.jobSectorName || ''}</TableCell>
                <TableCell>
                    <SingleSelect
                        key={0}
                        id="poolStatus"
                        placeholder="Select pool status..."
                        disabled={
                            !isActive ||
                            row?.statusId === '' ||
                            row?.statusId === undefined ||
                            poolStatus?.name?.toLowerCase() === 'rejected'
                        }
                        customClass="tableRowSelector"
                        menuItems={newStatuses || []}
                        selectedId={poolStatus?.id || ''}
                        selected={poolStatusDetails.find((el) => el.id === poolStatus?.id) || {}}
                        onChange={(chosenId) => onPoolStatusChange(chosenId)}
                    />
                </TableCell>
            </TableRow>
            {poolStatus?.name?.toLowerCase() !== 'rejected' ? (
                ''
            ) : (
                <TableRow>
                    <TableCell style={rowStyles} colSpan={7}>
                        <Collapse in={openId === row.id} timeout="auto" unmountOnExit>
                            <RejectionDetails row={row} onClose={onToggleDropdown} />
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
};

PoolTableRow.propTypes = {
    row: PropTypes.object,
    isActive: PropTypes.bool,
    openId: PropTypes.string,
    toggleDropdown: PropTypes.func,
    onUpdateParticipantStatus: PropTypes.func,
    onPoolApplicationRejectEditor: PropTypes.func,
    onPoolRejectedRow: PropTypes.func,
    rowCancelled: PropTypes.bool
};

export default PoolTableRow;
